import { random } from 'lodash-es';
import { type MouseEventHandler, useEffect, useRef } from 'react';

import { useUserRoleMapper } from '@/hook';
import { RGBA, VFlex, cn, storage } from '@/package';
import { useTenantStore } from '@/store';
import type { Tenant } from '@/type';

import style from './style';

const TenantItem = ({
  disabled,
  valid,
  message,
  onClick,
  className,
  Icon,
  iconClassName,
  ...tenant
}: Tenant & {
  disabled?: boolean;
  valid?: boolean;
  message?: string;
  onClick?: MouseEventHandler;
  className?: string;
  Icon?: ReactSVGComponent;
  iconClassName?: string;
}) => {
  const { tenantCountInfoMap } = useTenantStore();
  const { mapUserRole } = useUserRoleMapper();
  const bgColor = useRef<RGBA>(null);
  const textColor = useRef<string>(null);

  useEffect(() => {
    const storedBgColor = storage.local.get<number[]>(
      `${tenant.tenantID}_bg_color`,
    );
    const newBgColor =
      storedBgColor ??
      new RGBA(random(0, 255), random(0, 255), random(0, 255), 1).toArray();

    storage.local.set<number[]>(`${tenant.tenantID}_bg_color`, newBgColor);

    bgColor.current = new RGBA(...newBgColor);
    textColor.current =
      bgColor.current
        .toArray()
        .slice(0, 3)
        .reduce((acc, v) => acc + v, 0) <=
      (255 >> 1) * 3
        ? 'text-white'
        : 'text-black';
  }, []);

  return (
    <VFlex
      role="button"
      direction="col"
      gap={0.25}
      className="w-full"
      onClick={onClick}
    >
      <VFlex
        className={cn(style.tenantItemContainer(valid), className)}
        aria-disabled={disabled}
        items="center"
        gap={0.5}
      >
        <VFlex
          className={`h-8 w-8 rounded-sm text-body-xl font-bold ${textColor.current}`}
          style={{
            backgroundColor: `${bgColor.current}`,
          }}
          items="center"
          justify="center"
        >
          <span className="translate-y-[1px]">
            {tenant.tenantName[0]?.toUpperCase()}
          </span>
        </VFlex>
        <VFlex className="flex-1" direction="col" gap={0.25}>
          <div className={style.tenantItemText(disabled)}>
            {tenant.tenantName}
          </div>
          <VFlex
            className={style.tenantItemSubText(disabled)}
            gap={0.5}
            items="center"
          >
            <span>
              {tenantCountInfoMap[tenant.tenantID]?.userCount ?? 0} members
            </span>
            <div className="h-3 w-[1px] bg-gray-300"></div>
            <span>
              {mapUserRole(
                tenantCountInfoMap[tenant.tenantID]?.userRole ?? 'Guest',
              )}
            </span>
          </VFlex>
        </VFlex>
        {Icon && (
          <Icon
            className={
              iconClassName
                ? iconClassName
                : style.tenantItemArrowIcon(disabled)
            }
          />
        )}
      </VFlex>

      {message && (
        <div className={style.tenantItemMessage(valid)}>{message}</div>
      )}
    </VFlex>
  );
};

export default TenantItem;
